import React, { useState, useMemo, useEffect, useRef } from "react";
import { Heart, Search, ChevronDown, SlidersHorizontal, Bed, Bath, Ruler, MapPin, ArrowUpDown } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axiosInstance from "../../api/axiosInstance";
import { useSearchParams } from "react-router-dom";
import { propertyTypeLabels } from "../../utils/common";

const scopeLabels = {
  city: "City",
  zip: "ZIP Code",
  county: "County",
};

const limitOptions = [
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
  { label: "200", value: 200 },
  { label: "300", value: 300 },
];

const money = (n) => (n || n === 0 ? "$" + Number(n).toLocaleString("en-US") : "");

// ---- Simple rounded dot marker (color + size configurable) ----
const dotSvg = (fill, size, ring) => `
  <div style="
    width:${size}px;height:${size}px;border-radius:9999px;
    background:${fill};
    border:3px solid #fff;
    box-shadow:0 1px 4px rgba(0,0,0,0.35)${ring ? `, 0 0 0 4px ${ring}` : ""};
  "></div>
`;

const makeDotIcon = (fill, size = 18, ring = null) =>
  L.divIcon({
    html: dotSvg(fill, size, ring),
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });

const defaultIcon = makeDotIcon("#0284c7", 16); // sky-600
const activeIcon = makeDotIcon("#dc2626", 22, "rgba(220,38,38,0.35)"); // red-600, larger + haloed on select
const subjectIcon = makeDotIcon("#16a34a", 24, "rgba(22,163,74,0.35)"); // green-600, subject property

// ---- Colored cluster bubble icon (size + color scale with count) ----
const clusterTier = (count) => {
  if (count >= 100) return { bg: "#0b3556", ring: "rgba(11,53,86,0.35)", size: 54 }; // dark navy
  if (count >= 25) return { bg: "#0369a1", ring: "rgba(3,105,161,0.35)", size: 46 }; // sky-700
  if (count >= 10) return { bg: "#0284c7", ring: "rgba(2,132,199,0.35)", size: 40 }; // sky-600
  return { bg: "#38bdf8", ring: "rgba(56,189,248,0.35)", size: 34 }; // sky-400
};

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const { bg, ring, size } = clusterTier(count);
  return L.divIcon({
    html: `
      <div style="
        width:${size}px;height:${size}px;border-radius:9999px;
        background:${bg};box-shadow:0 0 0 6px ${ring};
        display:flex;align-items:center;justify-content:center;
        color:#fff;font-weight:700;font-size:${size >= 46 ? 14 : 12}px;
        border:2px solid #fff;
      ">${count}</div>
    `,
    className: "",
    iconSize: [size, size],
  });
};

const isValidLatLng = (lat, lng) => {
  const la = Number(lat);
  const ln = Number(lng);
  return Number.isFinite(la) && Number.isFinite(ln) && !(la === 0 && ln === 0);
};

// Flies the map to a given lat/lng whenever it changes (used for select-to-focus)
function MapFocus({ position, zoom = 15 }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, Math.max(map.getZoom(), zoom), { duration: 0.6 });
    }
  }, [position, map, zoom]);
  return null;
}

// Fits the map to bounds covering every loaded property (+ subject) once data arrives
function FitBounds({ points, trigger }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, map]);
  return null;
}

function FilterPill({ label, active }) {
  return (
    <button
      className={`
        flex items-center gap-1.5 px-4 py-2 rounded-full border 
        font-semibold text-sm whitespace-nowrap cursor-pointer
        ${active ? "border-sky-500 bg-sky-50 text-sky-700" : "border-gray-300 bg-white text-gray-700"}
      `}
    >
      {label}
      <ChevronDown size={14} strokeWidth={2.5} />
    </button>
  );
}

function PropertyCard({ p, isHovered, isSelected, onHover, onSelect, cardRef }) {
  return (
    <div
      ref={cardRef}
      onMouseEnter={() => onHover(p.uid)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onSelect(p.uid)}
      className={`
        bg-white rounded-xl overflow-hidden border shadow-sm flex flex-col cursor-pointer transition-all
        ${isSelected ? "border-sky-500 ring-2 ring-sky-300" : isHovered ? "border-sky-400 shadow-md" : "border-gray-200"}
      `}
    >
      <div className="text-left px-3.5 py-3">
        <div className="text-sm text-gray-700 leading-snug">
          <div className="font-semibold">{p.address}</div>
          <div className="text-gray-500">
            {p.city}, {p.state} {p.zip}
          </div>
        </div>
        {p.ownerName && <div className="text-xs text-sky-700 font-medium mt-1">{p.ownerName}</div>}
        <div className="flex gap-3 text-sm text-gray-600 mt-2 flex-wrap items-end">
          <span className="text-gray-400 capitalize">{p.sourceType}</span>
          {p.mailingAddress && <span className="text-gray-400 truncate">Mailing: {p.mailingAddress}</span>}
        </div>
      </div>
    </div>
  );
}

function PropertyCardSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm flex flex-col animate-pulse">
      <div className="px-3.5 py-3">
        <div className="h-3.5 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-1/2 mb-3" />
        <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-1/3" />
      </div>
    </div>
  );
}

export default function AddressOwnerGroupsV2() {
  const [search, setSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [sort, setSort] = useState("recommended");
  const [showRightPanel, setShowRightPanel] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const addressParam = searchParams.get("address");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const cardRefs = useRef({});
  const markerRefs = useRef({});
  const clusterGroupRef = useRef(null);

  const [formData, setFormData] = useState({
    address: searchParams.get("address") || "",
    flippersLimit: Number(searchParams.get("flippersLimit")) || 10,
    landlordsLimit: Number(searchParams.get("landlordsLimit")) || 10,
    property_type: searchParams.get("property_type") || "",
  });

  // ---- Dynamic header label from subjectProperty ----
  const subjectProperty = data?.subjectProperty || null;
  const headerLabel = subjectProperty ? subjectProperty.label || `${subjectProperty.address}, ${subjectProperty.city}, ${subjectProperty.state} ${subjectProperty.zip}` : addressParam || "your search";

  const subjectPosition = useMemo(() => {
    if (subjectProperty && isValidLatLng(subjectProperty.latitude, subjectProperty.longitude)) {
      return [Number(subjectProperty.latitude), Number(subjectProperty.longitude)];
    }
    return null;
  }, [subjectProperty]);

  // ---- Flatten flippers/landlords addresses into a single, deduped property list ----
  const properties = useMemo(() => {
    if (!data) return [];
    const byId = new Map();

    ["flippers", "landlords"].forEach((sourceType) => {
      const section = data[sourceType] || {};
      Object.keys(scopeLabels).forEach((scopeKey) => {
        const scopeData = section[scopeKey] || {};
        const rows = Array.isArray(scopeData) ? scopeData : scopeData.rows || [];
        rows.forEach((row) => {
          (row.addresses || []).forEach((addr) => {
            if (!isValidLatLng(addr.latitude, addr.longitude)) return;
            const uid = `${addr.property_id ?? `${addr.address}-${addr.zip}`}`;
            if (byId.has(uid)) return; // dedupe across sections/scopes
            byId.set(uid, {
              uid,
              propertyId: addr.property_id,
              address: addr.address,
              city: addr.city,
              state: addr.state,
              zip: addr.zip,
              lat: Number(addr.latitude),
              lng: Number(addr.longitude),
              ownerName: row.name,
              mailingAddress: row.mailing_address,
              sourceType: sourceType === "flippers" ? "Flipper" : "Landlord",
            });
          });
        });
      });
    });

    return Array.from(byId.values());
  }, [data]);

  const sorted = useMemo(() => {
    const list = [...properties];
    if (sort === "name_asc") list.sort((a, b) => (a.ownerName || "").localeCompare(b.ownerName || ""));
    if (sort === "name_desc") list.sort((a, b) => (b.ownerName || "").localeCompare(a.ownerName || ""));
    return list;
  }, [properties, sort]);

  const selectedProperty = useMemo(() => properties.find((p) => p.uid === selectedId) || null, [properties, selectedId]);

  const mapCenter = useMemo(() => {
    if (subjectPosition) return subjectPosition;
    if (sorted.length > 0) return [sorted[0].lat, sorted[0].lng];
    return [40.7128, -74.006];
  }, [subjectPosition, sorted]);

  // All lat/lng pairs (subject + every property) used to fit the map on initial load
  const allPoints = useMemo(() => {
    const pts = sorted.map((p) => [p.lat, p.lng]);
    if (subjectPosition) pts.unshift(subjectPosition);
    return pts;
  }, [sorted, subjectPosition]);

  const handleHover = (uid) => setHoveredId(uid);

  const handleSelect = (uid) => {
    setSelectedId(uid);
  };

  const handleMarkerClick = (uid) => {
    setSelectedId(uid);
    const el = cardRefs.current[uid];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Whenever selection changes, make sure the marker's popup actually shows on the map.
  // If it's currently hidden inside a cluster, zoom/spiderfy it into view first.
  useEffect(() => {
    if (!selectedId) return;
    const marker = markerRefs.current[selectedId];
    const clusterGroup = clusterGroupRef.current;
    if (!marker) return;

    if (clusterGroup && typeof clusterGroup.zoomToShowLayer === "function") {
      clusterGroup.zoomToShowLayer(marker, () => {
        marker.openPopup();
      });
    } else {
      marker.openPopup();
    }
  }, [selectedId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!addressParam?.trim()) {
        setError("Address is required in URL query parameter.");
        setData(null);
        return;
      }

      try {
        setLoading(true);
        setData(null);
        setError("");
        setSearchLoading(true);
        setSelectedId(null);
        setHoveredId(null);
        const params = {
          address: addressParam,
          flippers_limit: searchParams.get("flippersLimit") || undefined,
          landlords_limit: searchParams.get("landlordsLimit") || undefined,
          property_type: searchParams.get("property_type") || undefined,
        };
        const resp = await axiosInstance.get(`/api/dealmachine_properties/public/address-groups?include_addresses=true`, { params });
        setData(resp.data || null);
        setSearch(resp.data?.subjectProperty?.label || `${resp.data?.subjectProperty?.address}, ${resp.data?.subjectProperty?.city}, ${resp.data?.subjectProperty?.state} ${resp.data?.subjectProperty?.zip}`);
      } catch (err) {
        setData(null);
        setError(err.response?.data?.message || "Failed to load address data.");
      } finally {
        setLoading(false);
        setSearchLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams();

    if (formData.address.trim()) newParams.set("address", formData.address.trim());
    if (formData.flippersLimit) newParams.set("flippersLimit", formData.flippersLimit);
    if (formData.landlordsLimit) newParams.set("landlordsLimit", formData.landlordsLimit);
    if (formData.property_type) newParams.set("property_type", formData.property_type);

    setSearchParams(newParams);
  };

  const focusPosition = selectedProperty ? [selectedProperty.lat, selectedProperty.lng] : subjectPosition;

  return (
    <>
      <div className="w-screen h-screen bg-gray-100 p-4">
        <div className="flex flex-col h-full gap-4">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-5 py-3.5 text-sm">
            <form onSubmit={handleSubmit} className="!grid sm:!grid-cols-2 md:!grid-cols-3 lg:!grid-cols-4 gap-2 sm:gap-2 md:gap-2 max-w-7xl mx-auto">
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                <input value={formData.address} onChange={(e) => handleInputChange("address", e.target.value)} disabled={searchLoading} placeholder="Enter an address, city, zip" className="border-none outline-none bg-transparent w-full text-[#0B2436] disabled:cursor-not-allowed disabled:text-gray-500" />
              </div>
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                {formData.flippersLimit ? (
                  <label htmlFor="flippers" className="text-nowrap">
                    Flippers :
                  </label>
                ) : (
                  <></>
                )}
                <select className="w-full outline-none cursor-pointer" value={formData.flippersLimit} onChange={(e) => handleInputChange("flippersLimit", Number(e.target.value))} name="flippers">
                  <option value="" disabled>
                    -- Select Flippers --
                  </option>
                  {limitOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                {formData.landlordsLimit ? (
                  <label htmlFor="landlords" className="text-nowrap">
                    Landlords :
                  </label>
                ) : (
                  <></>
                )}
                <select className="w-full outline-none cursor-pointer" value={formData.landlordsLimit} onChange={(e) => handleInputChange("landlordsLimit", Number(e.target.value))} name="landlords">
                  <option value="" disabled>
                    -- Select Landlords --
                  </option>
                  {limitOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between gap-2 border border-gray-300 rounded px-3 py-2 bg-white">
                {formData.property_type ? (
                  <label htmlFor="type" className="text-nowrap">
                    Type :
                  </label>
                ) : (
                  <></>
                )}
                <select className="w-full outline-none cursor-pointer" value={formData.property_type || ""} onChange={(e) => handleInputChange("property_type", e.target.value)} name="type">
                  <option value="">-- Select Property Type --</option>
                  {Object.entries(propertyTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 md:col-span-3 lg:col-span-4 flex justify-center">
                <button className="flex items-center justify-center gap-1 px-4 py-1.5 rounded border border-sky-400 bg-sky-400 text-white hover:bg-sky-500 hover:border-sky-500 ease-in-out duration-200 transition-all min-w-52 text-center disabled:cursor-not-allowed disabled:border-sky-300 disabled:bg-sky-300" type="submit" disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </form>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 overflow-hidden flex-1 min-h-0">
            {/* Left */}
            <div
              className={`
      rounded-lg shadow overflow-hidden col-span-7 h-full
      ${showRightPanel ? "hidden md:block" : "block"}
    `}
            >
              <MapContainer center={mapCenter} zoom={subjectPosition ? 13 : 8} className="w-full h-full">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />

                {/* Focus map on all properties once a new dataset finishes loading */}
                {!loading && allPoints.length > 0 && <FitBounds points={allPoints} trigger={data} />}

                {subjectPosition && (
                  <Marker position={subjectPosition} icon={subjectIcon}>
                    <Popup>
                      <div className="font-semibold">{headerLabel}</div>
                      <div className="text-xs text-gray-500">Subject property</div>
                    </Popup>
                  </Marker>
                )}

                <MarkerClusterGroup ref={clusterGroupRef} chunkedLoading maxClusterRadius={60} spiderfyOnMaxZoom disableClusteringAtZoom={17} iconCreateFunction={createClusterIcon}>
                  {sorted.map((p) => (
                    <Marker
                      key={p.uid}
                      ref={(el) => {
                        if (el) markerRefs.current[p.uid] = el;
                      }}
                      position={[p.lat, p.lng]}
                      icon={p.uid === selectedId ? activeIcon : defaultIcon}
                      zIndexOffset={p.uid === selectedId ? 1000 : 0}
                      eventHandlers={{
                        mouseover: () => handleHover(p.uid),
                        mouseout: () => handleHover(null),
                        click: () => handleMarkerClick(p.uid),
                      }}
                    >
                      <Popup>
                        <div className="font-semibold">{p.address}</div>
                        <div className="text-xs text-gray-500">
                          {p.city}, {p.state} {p.zip}
                        </div>
                        {p.ownerName && <div className="text-xs text-sky-700 mt-1">{p.ownerName}</div>}
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              </MapContainer>
            </div>

            {/* Right */}
            <div
              className={`
      bg-white rounded-lg shadow overflow-y-auto col-span-5
      w-full relative
      ${showRightPanel ? "block" : "hidden md:block"}
    `}
            >
              <div className="flex items-baseline justify-between mb-3.5 sticky top-0 bg-white p-4 rounded-lg z-[1]">
                <h2 className="text-lg font-extrabold text-[#0B2436] m-0">{loading ? `Searching near ${headerLabel}...` : `${sorted.length} owners near ${headerLabel}`}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mx-4 mb-4">
                {loading ? Array.from({ length: 10 }).map((_, i) => <PropertyCardSkeleton key={i} />) : sorted.map((p) => <PropertyCard key={p.uid} p={p} isHovered={hoveredId === p.uid} isSelected={selectedId === p.uid} onHover={handleHover} onSelect={handleSelect} cardRef={(el) => (cardRefs.current[p.uid] = el)} />)}
                {!loading && sorted.length === 0 && data && <div className="text-sm text-gray-500 col-span-2 text-center py-8">No mappable addresses found for this search.</div>}
              </div>
            </div>
          </div>

          <button onClick={() => setShowRightPanel((prev) => !prev)} className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 shadow-lg active:scale-95 z-[9999] flex items-center justify-center gap-1 px-4 py-1.5 rounded border border-sky-400 bg-sky-400 text-white hover:bg-sky-500 hover:border-sky-500 ease-in-out duration-200 transition-all text-sm min-w-52">
            {showRightPanel ? "Show Map" : "Show Properties"}
          </button>
        </div>
      </div>
    </>
  );
}
